import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <p className="text-foreground-500">文章不存在</p>
          <Link to="/" className="text-primary-500 text-sm mt-2 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find(c => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '秘密教学',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />
      <main>
        <div className="pt-20 pb-4 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-8 md:py-12 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-semibold
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
                prose-strong:text-foreground-950 prose-strong:font-semibold
                prose-li:text-foreground-700 prose-li:leading-relaxed
                prose-ul:my-4 prose-ol:my-4
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h3 className="text-xl font-semibold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-8 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <Link
              to={category ? `/category/${category.slug}` : '/'}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-200/70 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {category ? `返回 ${category.name}` : '返回首页'}
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}